'use client';

import React from 'react';
import { useProfile } from 'src/context/providers/ProfileProvider';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useGetFreshFacesLocationsQuery } from 'src/api/redux';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import FreshFacesLocationRow from './FreshFacesLocationRow';

const PREVIEW_MAX_ITEMS = 2;

const CompactFreshFacesLocationsPreview: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();

  const { providerId: entityId } = getEntityIdentifiers(entityDetails);
  const strategy = widgetConfig?.widget?.fresh_faces_strategy;
  const strategyId = strategy?.id;
  const lookBackMonths = widgetConfig?.widget?.look_back_months;

  const { data, isLoading } = useGetFreshFacesLocationsQuery(
    {
      entityId: entityId!,
      strategyId: strategyId!,
      lookBackMonths: lookBackMonths!,
    },
    { skip: !entityId || !strategyId || lookBackMonths == null }
  );

  const locations = (data ?? []).slice(0, PREVIEW_MAX_ITEMS);

  if (isLoading) {
    return <WidgetLoading data-testid="compact-fresh-faces-locations-preview-loading" aria-label="Loading fresh faces locations" />;
  }

  if (locations.length === 0) {
    return <WidgetEmpty data-testid="compact-fresh-faces-locations-preview-empty" />;
  }

  return (
    <div
      className="flex flex-col"
      data-testid="compact-fresh-faces-locations-preview"
    >
      <div className="flex items-center justify-between pb-1 border-b border-neutral-100">
        <span className="text-xs font-semibold text-tertiary">Location</span>
        <div className="flex gap-4">
          <span className="text-xs font-semibold text-tertiary min-w-14 text-end">
            Claims
          </span>
          <span className="text-xs font-semibold text-tertiary min-w-20 text-end">
            First Billed
          </span>
        </div>
      </div>
      {locations.map((location, index) => (
        <FreshFacesLocationRow
          key={`${location.provider_id}-${index}`}
          location={location}
        />
      ))}
    </div>
  );
};

export default CompactFreshFacesLocationsPreview;
